import { CONFIG } from './config';
import { getIdToken } from './auth';

async function req<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const token = await getIdToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const res = await fetch(`${CONFIG.apiBase}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText} — ${text.slice(0, 200)}`);
  }
  return text ? (JSON.parse(text) as T) : ({} as T);
}

// ---- Types ----

export interface User {
  name: string;
  prefix: string;
  seq: number;
  createdAt: string;
}
export interface ApplovinConfig {
  sdkKey?: string;
  rewardUnitId?: string;
  interstitialUnitId?: string;
}
export interface LineSlot {
  slotId: string;
  targetCpm: number | null; // null = 上限なし
}
export interface LineConfig {
  slots: LineSlot[];
}
export interface App {
  package: string;
  name: string;
  brand?: string;
  applovin?: ApplovinConfig;
  line?: LineConfig;
  enabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
}
export interface RotationEntry {
  network: string;
  adUnitId?: string;
}
export interface Slot {
  index: number;
  startMinutes: number;
  endMinutes: number;
  appPackage: string;
  masked: boolean;
}
export interface DeviceListItem {
  name: string;
  prefix: string;
  createdAt?: string;
  lastSeenAt?: string;
  hidden?: boolean;
}
export interface DeviceDetail {
  device: DeviceListItem;
  inventory: { apps: string[]; postedAt: string } | null;
  shift: { date: string; slots: Slot[]; maskedSlots: number[] } | null;
  recentViews: Array<{
    ts: string;
    network: string;
    adUnitId: string;
    appPackage: string;
  }>;
}
export interface RecentViewsResp {
  minutes: number;
  items: Array<{
    device: string;
    network: string;
    adUnitId: string;
    appPackage: string;
    count: number;
  }>;
}
export interface HourlyStatsResp {
  hours: string[]; // yyyymmddHH の昇順配列
  items: Array<{
    hour: string;
    network: string;
    appPackage: string;
    count: number;
  }>;
}
export interface LanDailyResp {
  dates: string[]; // yyyymmdd の昇順配列
  items: Array<{
    date: string;
    brand: string;
    imp: number;
    click: number;
    revenue: number;
  }>;
}
export interface StatsResp {
  date?: string;
  week?: string;
  items: Array<{
    device: string;
    network: string;
    appPackage: string;
    count: number;
  }>;
}

// ---- Users ----
export const listUsers = (q?: string) =>
  req<{ users: User[] }>('GET', `/v1/users${q ? `?q=${encodeURIComponent(q)}` : ''}`);
export const allocateUser = (prefix: string) =>
  req<User>('POST', '/v1/users', { prefix });

// ---- Apps ----
export const listApps = () => req<{ apps: App[] }>('GET', '/v1/apps');
export const createApp = (body: {
  package: string;
  name: string;
  brand?: string;
}) => req<App>('POST', '/v1/apps', body);
export const updateApp = (
  pkg: string,
  body: {
    name?: string;
    brand?: string;
    applovin?: ApplovinConfig;
    line?: LineConfig;
    enabled?: boolean;
  },
) => req<{ ok: boolean }>('PUT', `/v1/apps/${encodeURIComponent(pkg)}`, body);

// ---- Rotation ----
export const getRotation = () =>
  req<{ entries: RotationEntry[]; updatedAt?: string }>('GET', '/v1/rotation');
export const putRotation = (entries: RotationEntry[]) =>
  req<{ entries: RotationEntry[] }>('PUT', '/v1/rotation', { entries });

// ---- TapRates ----
export const getTapRates = () =>
  req<Record<string, number>>('GET', '/v1/tap-rates');
export const putTapRates = (rates: { line?: number; applovin?: number; admob?: number }) =>
  req<{ ok: boolean }>('PUT', '/v1/tap-rates', rates);

// ---- Masks ----
export const getMasks = () => req<Record<string, number>>('GET', '/v1/masks');
export const putMask = (prefix: string, maskRatio: number) =>
  req<{ prefix: string; maskRatio: number }>(
    'PUT',
    `/v1/masks/${encodeURIComponent(prefix)}`,
    { maskRatio },
  );

// ---- Devices ----
export const listDevices = () =>
  req<{ devices: DeviceListItem[] }>('GET', '/v1/devices');
export const getDevice = (name: string) =>
  req<DeviceDetail>('GET', `/v1/devices/${encodeURIComponent(name)}`);
export const updateDeviceHidden = (name: string, hidden: boolean) =>
  req<{ name: string; hidden: boolean }>(
    'PATCH',
    `/v1/devices/${encodeURIComponent(name)}`,
    { hidden },
  );

// ---- Stats ----
export const recentViews = (minutes = 60) =>
  req<RecentViewsResp>('GET', `/v1/stats/recent-views?minutes=${minutes}`);
export const hourlyStats = (hours = 24) =>
  req<HourlyStatsResp>('GET', `/v1/stats/hourly?hours=${hours}`);
export const lanDaily = (days = 30) =>
  req<LanDailyResp>('GET', `/v1/stats/lan-daily?days=${days}`);
export const admobDaily = (days = 30) =>
  req<LanDailyResp>('GET', `/v1/stats/admob-daily?days=${days}`);
/** items[].brand には広告ソース名が入る (グラフ共通化のため)。 */
export const admobMediationDaily = (days = 30, brand = '') =>
  req<LanDailyResp & { brands?: string[] }>(
    'GET',
    `/v1/stats/admob-mediation-daily?days=${days}${brand ? `&brand=${encodeURIComponent(brand)}` : ''}`,
  );
export const dailyStats = (date?: string) =>
  req<StatsResp>('GET', `/v1/stats/daily${date ? `?date=${date}` : ''}`);
export const weeklyStats = (week?: string) =>
  req<StatsResp>('GET', `/v1/stats/weekly${week ? `?week=${week}` : ''}`);
